import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    GraphQLResponse,
    Pagination,
    ProtoApiService
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    Date as DateModel,
    SortDirection
} from '@lsq-frontend/remote-services/all-java/common-mk0';
import { BankAccountType } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { convertDateToLocalDate } from '../../helpers/date-mapping';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    mapBuyerStatusFiltersToPayableLegacyStatusInput,
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus,
    mapPayableStatusToBuyerApprovalStatus
} from '../../helpers/status-mapping';
import { ListInvoiceSummaryForBuyerResponse } from '../../model/list-invoice-summary-for-buyer-response';
import {
    BuyerInvoiceStatus2,
    InvoiceSummary2,
    PayablesInput,
    PayablesResponse,
    TransferType
} from '../../model/models';
import { deDuplicatePendingCreditMemos, calculateAmount } from '../../helpers/credit-memo-mapping';

export class ListInvoiceSummaryForBuyerRequest2 {
    public refBuyerKeys: string[];
    public buyerAccountKeys: string[];
    public sellerKeys: string[];
    public buyerInvoiceStatus: BuyerInvoiceStatus2[];
    public invoiceNumberFilter: string;
    public pagination: Pagination;
    public includePendingPayables: boolean;
    constructor(init?: Partial<ListInvoiceSummaryForBuyerRequest2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}
@Injectable({ providedIn: 'root' })
export class ListInvoiceSummaryForBuyerService extends ProtoApiService<
    ListInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerResponse
> {
    public requestType = ListInvoiceSummaryForBuyerRequest2;
    public responseType = ListInvoiceSummaryForBuyerResponse;
    public endpointName = 'ListInvoiceSummaryForBuyer';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListInvoiceSummaryForBuyer';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListInvoiceSummaryForBuyerRequest2
    ): { query: string } | ListInvoiceSummaryForBuyerRequest2 {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection
            } = req.pagination;

            const input: PayablesInput = omitBy(
                {
                    legacyStatuses: mapBuyerStatusFiltersToPayableLegacyStatusInput(
                        req.buyerInvoiceStatus
                    ),
                    companyFilter: {
                        buyerIds: req.refBuyerKeys ? req.refBuyerKeys : [],
                        buyerAccountIds: req.buyerAccountKeys ? req.buyerAccountKeys : [],
                        supplierIds: req.sellerKeys ?? []
                    },
                    payableNameSearch: req.invoiceNumberFilter ?? '',
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] || SortDirection.ASCENDING
                        ),
                        secondarySortColumn: new EnumType(
                            mapBuyerPayableSortColumn(secondarySortColumn)
                        ),
                        secondarySortDirection: new EnumType(
                            SortDirection[secondarySortDirection] || SortDirection.ASCENDING
                        )
                    },
                    includePendingPayables: req.includePendingPayables
                },
                isNil
            );
            const money = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        id: true,
                        name: true,
                        amount: money,
                        amountLessSupplierDiscountFee: money,
                        status: true,
                        statusLegacy: true,
                        invoiceDateParent: true,
                        fundingDate: true,
                        earlyPaymentDate: true,
                        originalDueDate: true,
                        payLaterAdjustedDueDate: true,
                        dueInDays: true,
                        buyerDiscountFeeRate: true,
                        buyerDiscountFee: money,
                        supplierDiscountFee: money,
                        isGroup: true,
                        nameSearchMatchedNested: true,
                        buyerFundingChargeTotal: money,
                        feesDue: money,
                        canBeRevoked: true,
                        ineligibilityReason: true,
                        relationship: {
                            supplier: { id: true, company: { name: true } },
                            buyer: { id: true, company: { name: true } }
                        },
                        approvals: {
                            id: true,
                            invoice: {
                                date: true,
                                invoiceNumber: true,
                                amount: money
                            }
                        },
                        creditMemos: {
                            id: true,
                            allocatedAmount: money,
                            amount: money
                        }
                    },
                    totals: { invoiceCount: true, payableCount: true }
                }
            });

            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | ListInvoiceSummaryForBuyerResponse
    ): ListInvoiceSummaryForBuyerResponse {
        if (this._isPayLaterEnabled) {
            const { rows: payables, totals } = (res as GraphQLResponse<PayablesResponse>).data
                .payables;

            return new ListInvoiceSummaryForBuyerResponse({
                ok: true,
                totalCount: totals.payableCount,
                totalCountBuyerInvoices: totals.invoiceCount,
                rows: payables.map((payable) => {
                    const { approvals, creditMemos, isGroup } = payable;
                    const { buyer, supplier } = payable.relationship;

                    let payableAmount = payable.amount;
                    let deduplicatedCreditMemos = creditMemos;

                    // Detect if this is a pending payable group
                    if (payable.approvals.length == 0 && creditMemos.length > 0) {
                        deduplicatedCreditMemos = deDuplicatePendingCreditMemos(creditMemos);
                        payableAmount = calculateAmount(deduplicatedCreditMemos);
                    }

                    const invoiceDate = convertDateToLocalDate(payable.invoiceDateParent);
                    const invoiceDateNew =
                        invoiceDate &&
                        new DateModel({
                            day: invoiceDate.getDate(),
                            month: invoiceDate.getMonth() + 1,
                            year: invoiceDate.getFullYear()
                        });

                    const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
                    const invoiceDueDateNew =
                        invoiceDueDate &&
                        new DateModel({
                            day: invoiceDueDate.getDate(),
                            month: invoiceDueDate.getMonth() + 1,
                            year: invoiceDueDate.getFullYear()
                        });

                    let earlyPaymentDate = convertDateToLocalDate(payable.earlyPaymentDate);
                    let earlyPaymentDateNew =
                        earlyPaymentDate &&
                        new DateModel({
                            day: earlyPaymentDate.getDate(),
                            month: earlyPaymentDate.getMonth() + 1,
                            year: earlyPaymentDate.getFullYear()
                        });
                    let payLaterDueDate = convertDateToLocalDate(payable.payLaterAdjustedDueDate);

                    let bankAccountType: BankAccountType;
                    if (payable.transfers?.[0]) {
                        bankAccountType = this._translateBankAccountType(
                            payable.transfers[0].transferType
                        );
                    }

                    // TODO: implement these mappings
                    const buyerPaidTransactionFee = false;
                    const groupedInvoiceNumberMatched = payable.nameSearchMatchedNested;

                    return new InvoiceSummary2({
                        isGroup,
                        invoiceDate,
                        invoiceDateNew,
                        invoiceDueDate,
                        invoiceDueDateNew,
                        earlyPaymentDate,
                        earlyPaymentDateNew,
                        payLaterDueDate,
                        invoiceFees: payable.feesDue,
                        refInvoiceKey: payable.id, // isGroup ? payable.id : firstApproval.id,
                        buyerName: buyer.company.name,
                        invoiceNumber: payable.name, // isGroup ? payable.name : firstApproval.invoice.invoiceNumber,
                        dueIn: payable.dueInDays,
                        feeRateHundredthOfBps: payable.buyerDiscountFeeRate * 100,
                        sellerName: supplier.company.name,
                        sellerKey: supplier.id,
                        refBuyerKey: buyer.id,
                        invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                            payable.statusLegacy
                        ),
                        fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                            payable.statusLegacy
                        ),
                        buyerApprovalStatus: mapPayableStatusToBuyerApprovalStatus(payable.status),
                        earlyPaymentStatus: mapPayableStatusLegacyToEarlyPmtStatus(
                            payable.statusLegacy
                        ),
                        buyerDiscountAmount: payable.buyerDiscountFee,
                        supplierDiscountAmount: payable.supplierDiscountFee,
                        buyerDiscountPercentage: payable.buyerDiscountFeeRate * 100,
                        invoiceAmount: payableAmount, // TODO: Summing payable.approvals[].invoice.amount would be more correct
                        finalApprovedAmount: payableAmount, // TODO: Not really the correct field for this, but the non-greenfield version of the invoice table still looks at this field so they'll need to be reconciled somehow
                        discountAmount: payable.supplierDiscountFee,
                        availableAmount: payable.amountLessSupplierDiscountFee, // TODO: The non-greenfield version of this endpoint does not populate this field. This will be need to be reconciled.
                        canBeRevoked: payable.canBeRevoked,
                        buyerPaidTransactionFee, // TODO: add to graphql
                        groupedInvoiceNumberMatched,
                        bankAccountType, // TODO: add to graphql
                        totalGroupedInvoiceCount: approvals.length + creditMemos.length,
                        ineligibilityReason: payable.ineligibilityReason
                    });
                })
            });
        }
        return res as ListInvoiceSummaryForBuyerResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    // TODO: translate to proper status enum
    private _translateBankAccountType(transferType: TransferType): BankAccountType {
        return BankAccountType[transferType] || BankAccountType.UNKNOWN_BANK_ACCOUNT_TYPE;
    }
}
